import "./Home.css";
import { Globe, GraduationCap, DollarSign, TrendingUp, Heart, FileX } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import Story from "./Story";
import Videos from "./Videos";
import Cta from "./Cta";
import Footer from "./Footer";
import { Link, useLocation } from "react-router-dom";
import Map from "./components/Map";
import Testimonial from "./components/Testimonial";
import Slider from "./components/Slider";
import WhyChooseSection from "./WhyChooseSection ";
import HowItWorksSection from "./HowItWorksSection";
import { useEffect, useState } from "react";
import ReviewSection from "./ReviewSection";
import './Reviews.css';
import VideoSection from "./VideoSection";
import "./VideoSection.css";
import PricingSection from "./PricingSection";

const Home = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Check for the 'isLoginTrue' key in local storage to determine login status.
    const loginStatus = localStorage.getItem('isLoginTrue');
    if (loginStatus === 'true') {
      setIsLoggedIn(true);
    }
  }, []);


  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true
  };

  const featuresData = [
    {
      icon: <Globe className="wcs-card-icon-svg" />,
      image: 'photos/ah7.webp',
      title: 'Smart Country Matching',
      description: 'AI recommends the best countries to study abroad based on your profile.',
    },
    {
      icon: <GraduationCap className="wcs-card-icon-svg" />,
      image: 'photos/ah9.webp',
      title: 'University Selection',
      description: 'Find top colleges that match your marks, goals, and budget.',
    },
    {
      icon: <DollarSign className="wcs-card-icon-svg" />,
      image: 'photos/ah1.webp',
      title: 'Cost & Budget Analysis',
      description: 'Get tuition fees, living costs, and total expenses by country.',
    },
    {
      icon: <TrendingUp className="wcs-card-icon-svg" />,
      image: 'photos/ah7.webp',
      title: 'Career & PR Prospects',
      description: 'See job options, salary data, and PR chances after study.',
    },
  ];

   const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      <Helmet>
        <meta name="title" content="Home | Get Next Visa - AI Powered Student Visa Report" />
                <title>Home | Get Next Visa - AI Powered Student Visa Report</title>
        <meta name="description" content="Get personalized, AI-powered visa reports. Discover the best countries and colleges for your study abroad journey." />
      </Helmet>

      {/* section 1 */}
      <section className="hero-section">
        <div className="container-hero">
          <div className="content">
            <div className="badge">AI-Powered Immigration Planning </div>
            <h1 className="main-heading1">
              Your Gateway to
              <br />
              <span className="highlight">Next Visa</span>
            </h1>
            <p className="description">
              Dreaming of studying abroad? Let our AI build your perfect plan in seconds—meet your top country fits, scholarship matches, visa roadmap and post‑study career path, all in one easy report.            </p>
            <div className="button-group">
            {isLoggedIn ? (
                <Link to="/dashboard" className="primary-btn" aria-label="Get My Report">
                  Get My Report →
                </Link>
              ) : (
                <a href="#" className="primary-btn trigger-login-popup" aria-label="Get My Report">
                  Get My Report →
                </a>
              )}

              <a href="/Itwork" className="secondary-btn" aria-label="How It Works">
                How It Works
              </a>
            </div>
            <div className="stats">
              <div className="stat-item">
                <div className="stat-number">10,000+</div>
                <div className="stat-label">Students Guided</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">95%</div>
                <div className="stat-label">Visa-Ready Reports</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">1 Lakh+</div>
                <div className="stat-label">Colleges Covered</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">200+</div>
                <div className="stat-label">Countries</div>
              </div>
            </div>
          </div>

          {/* right section  */}
          <div className="feature-card-wrapper">
            <div className="feature-card">
              <div className="feature-flex">
                <div className="feature-items">
                  <div className="icon">
                    {/* Icon for AI: a robot or brain */}
                    <i className="fa-solid fa-robot"></i>
                  </div>
                  <div className="text">
                    <h3>AI-Tailored Recommendations</h3>
                  </div>
                </div>

                <div className="feature-items">
                  <div className="icon">
                    {/* Icon for Visa: a passport */}
                    <i className="fa-solid fa-passport"></i>
                  </div>
                  <div className="text">
                    <h3>Visa Planning & Insights</h3>
                  </div>
                </div>

                <div className="feature-items">
                  <div className="icon">
                     {/* Icon for Scholarship: a graduation cap */}
                    <i className="fa-solid fa-graduation-cap"></i>
                  </div>
                  <div className="text">
                    <h3>Scholarship Opportunities</h3>
                  </div>
                </div>

              </div>
              <div className="feature-flex-1">

                <div className="feature-items">
                  <div className="icon">
                    {/* Icon for Cost/Job Market: a line chart */}
                    <i className="fa-solid fa-chart-line"></i>
                  </div>
                  <div className="text">
                    <h3>Cost, ROI & Job Market Insights</h3>
                  </div>
                </div>

                <div className="feature-items">
                  <div className="icon">
                    {/* Icon for Career: a briefcase */}
                    <i className="fa-solid fa-briefcase"></i>
                  </div>
                  <div className="text">
                    <h3>Post-Study Career & PR Forecast</h3>
                  </div>
                </div>

                <div className="feature-items">
                  <div className="icon">
                    {/* Icon for College: a university building */}
                    <i className="fa-solid fa-building-columns"></i>
                  </div>
                  <div className="text">
                    <h3>Course & College Recommendations</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <VideoSection/>

      <Map isLoggedIn={isLoggedIn} />

      {/* section 2.0 */}
      <section className="com-1">
        <div className="comparison-section">
          <div className="tagline">
            Why Choose Get Next Visa</div>
          <h1 className="heading">
            Why Get Next Visa Beats  <br /><span className="highlight">Traditional Consultants</span>
          </h1>
          <p className="subheading">
            Our AI‑powered platform delivers honest, up‑to‑date study‑abroad advice—no commissions, no hidden agendas—so Indian students can plan with clarity and confidence.
          </p>
          <div className="comparison-container">
            <div className="card left-card">
              <div className="card-header">
                <img src="/photos/gnv_logo.png" alt="canada work permit" loading="lazy" />
                <h3>Get Next Visa</h3>
                <p className="blue-text">(AI‑Powered & Unbiased)</p>
              </div>
              <ul className="features">
                <li>
                  <span className="fre-icon">
                    <img src="/photos/sg3.png" alt="canada visa application online" loading="lazy" />
                  </span>
                  <strong>Smart University Matching</strong>
                  <br />
                  AI scans 1,000+ programs to find your perfect fit—zero bias.
                </li>

                <li>
                  <span className="fre-icon">
                    <img src="/photos/sg2.png" alt="apply for canada visa" loading="lazy" />
                  </span>{" "}
                  <strong>No Hidden Deals</strong>
                  <br />
                  We never recommend based on commissions or kickbacks.
                </li>
                <li>
                  <span className="fre-icon">
                    <img src="/photos/unt1.png" alt="super visa canada" loading="lazy" />
                  </span>{" "}
                  <strong>Made for You</strong>
                  <br />
                  Suggestions tailored to your marks, budget, and career goals.
                </li>
                <li>
                  <span className="fre-icon">
                    <img src="/photos/sg6.png" alt="canada work permit visa" loading="lazy" />
                  </span>{" "}
                  <strong>Real-Time Data </strong>
                  <br />
                  Live updates on fees, acceptance rates, and visa odds.
                </li>
                <li>
                  <span className="fre-icon">
                    <img src="/photos/sg1.png" alt="canada visa for indians" loading="lazy" />
                  </span>{" "}
                  <strong>200+ Countries Covered </strong>
                  <br />
                  From Canada to Germany to Australia—all in one place.
                </li>
              </ul>
              <div className="footer-note blue-text">
                Your Success, Our Priority
              </div>
            </div>
            <div className="card right-card">
              <div className="card-header">
                <img src="/photos/consultant.jpg" alt="pr in canada" loading="lazy" />
                <h3>Traditional Consultants</h3>
                <p className="gray-text"> (Commission‑Driven)</p>
              </div>
              <ul className="features">
                <li>
                  <span className="fre-icon">
                    <img src="/photos/bf1.png" alt="canada visa price" loading="lazy" />
                  </span>
                  <strong>Promoted Colleges Only</strong>
                  <br />
                  They push schools that pay them the highest fees.
                </li>
                <li>
                  <span className="fre-icon">
                    <img src="/photos/bf2.png" alt="canada work visa cost" loading="lazy" />
                  </span>
                  <strong>Limited Choices
                  </strong>
                  <br />
                  Usually just 10–20 options that may not suit you.
                </li>
                <li>
                  <span className="fre-icon">
                    <img src="/photos/bf3.png" alt="eligibility for pr in canada" loading="lazy" />
                  </span>
                  <strong>One‑Size‑Fits‑All
                  </strong>
                  <br />
                  Same generic advice for every student.
                </li>
                <li>
                  <span className="fre-icon">
                    <img src="/photos/bf4.png" alt="education visa" loading="lazy" />
                  </span>
                  <strong>Outdated Information</strong>
                  <br />
                  Often based on old PDFs or brochures.
                </li>
                <li>
                  <span className="fre-icon">
                    <img src="/photos/bf5.png" alt="canada visa" loading="lazy" />
                  </span>
                  <strong>No Transparency</strong>
                  <br />
                  You never know who they’re really working for.
                </li>
              </ul>
              <div className="footer-note gray-text">
                Their Profit, Your Compromise
              </div>
            </div>
          </div>
        </div>
        {isLoggedIn ? (
          <Link to="/dashboard" className="primary-btn" aria-label="Try Now" style={{ display: "flex", width: "fit-content", justifyContent: "center", margin: "0 auto", cursor: "pointer", marginTop: "40px" }}>
            Try Now →
          </Link>
        ) : (
          <a className="primary-btn trigger-login-popup" aria-label="Try Now" style={{ display: "flex", width: "fit-content", justifyContent: "center", margin: "0 auto", cursor: "pointer", marginTop: "40px" }}>
            Try Now →
          </a>
        )}
      </section>

     <WhyChooseSection isLoggedIn={isLoggedIn} />
      <HowItWorksSection isLoggedIn={isLoggedIn}/>
      <PricingSection/>
      <ReviewSection/>
      <Testimonial />

      <section className="landing-hero-container">
        <div className="animated-background-icon decorative-floating-element" style={{ top: "15%", left: "12%" }}>✈️</div>
        <div className="animated-background-icon decorative-floating-element" style={{ top: "25%", right: "18%" }}>🎓</div>
        <div className="animated-background-icon decorative-floating-element" style={{ bottom: "35%", left: "10%" }}>🌍</div>
        <div className="animated-background-icon decorative-floating-element" style={{ bottom: "20%", right: "15%" }}>📚</div>
        <div className="animated-background-icon decorative-floating-element" style={{ top: "45%", left: "8%" }}>⭐</div>
        <div className="animated-background-icon decorative-floating-element" style={{ top: "60%", right: "12%" }}>🚀</div>
        <div className="animated-background-icon decorative-floating-element" style={{ top: "35%", left: "85%" }}>✨</div>
        <div className="animated-background-icon decorative-floating-element" style={{ bottom: "45%", right: "8%" }}>🎯</div>
        <div className="main-content-wrapper">
          <div className="journey-badge">
            Your Journey Starts Here
          </div>
          <h1 className="hero-main-title">
            Discover Your Ideal Country,
            <br />
            <span className="title-highlight-text">   College & Visa Plan</span>
          </h1>
          <p className="hero-description-text">
            Join 10,000+ Indian students who found their ideal country, college, and visa plan with our AI‑powered platform. Your personalized roadmap is just one click away!
          </p>
          <div className="call-to-action-buttons">
            {isLoggedIn ? (
              <Link to="/dashboard" className="action-buttn primary-action-buttn" aria-label="Start Your Journey Now" style={{ cursor: "pointer" }}>
                <img src="/photos/tag.png" alt="USA Visa" loading="lazy" />
                Start Your Journey Now →
              </Link>
            ) : (
              <a className="action-buttn primary-action-buttn trigger-login-popup" aria-label="Start Your Journey Now" style={{ cursor: "pointer" }}>
                <img src="/photos/tag.png" alt="USA Visa" loading="lazy" />
                Start Your Journey Now →
              </a>
            )}
            <a href="/Pricing" className="action-buttn secondary-action-buttn" aria-label="Learn More">
              View All Plans
            </a>
          </div>
          <div className="statistics-grid-container">
            <div className="statistic-item-card">
              <span className="statistic-number-display">10,000+ </span>
              <span className="statistic-label-text">Students Helped</span>
            </div>
            <div className="statistic-item-card">
              <span className="statistic-number-display">95%</span>
              <span className="statistic-label-text">Success Rate</span>
            </div>
            <div className="statistic-item-card">
              <span className="statistic-number-display">50+</span>
              <span className="statistic-label-text">Countries Covered</span>
            </div>
             <div className="statistic-item-card">
              <span className="statistic-number-display">10+</span>
              <span className="statistic-label-text"> Covered</span>
            </div>
          </div>
        </div>
      </section>

      <section className="custom-cta-section">
        <div className="custom-cta-container">
          <h2 className="custom-cta-title">Experience the Difference Today</h2>
          <p className="custom-cta-description">
            Join thousands of Indian students who chose AI-powered study abroad planning over commission-based advice.
          </p>
          <div className="custom-cta-buttons">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                aria-label="Get Unbiased Report"
                className="custom-cta-btn custom-cta-btn-primary"
              >
                Get Unbiased Report
                <span className="custom-btn-icon">→</span>
              </Link>
            ) : (
              <a
                aria-label="Get Unbiased Report"
                className="custom-cta-btn custom-cta-btn-primary trigger-login-popup"
              >
                Get Unbiased Report
                <span className="custom-btn-icon">→</span>
              </a>
            )}
            <a
              href="/Itwork" aria-label="Instagram"
              className="custom-cta-btn custom-cta-btn-secondary"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      <Cta />
      {/* <Footer /> */}
    </>
  );
};

export default Home;